import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SynapseService } from 'src/app/core/services/synapse';
import {
  Duration,
  GetPageOptions,
  IP95UtilizationService,
  Loading,
  P95UtilizationRecord,
  Records,
  ServiceError,
} from './p95-utilization.service.types';

const DEFAULT_DURATION: Duration = '1m';

type P95UtilizationApiResponse = {
  items?: unknown[];
};

@Injectable({
  providedIn: 'root',
})
export class P95UtilizationService implements IP95UtilizationService {
  private readonly synapseService$ = inject(SynapseService);

  public readonly records$ = signal<Records>([]);

  public readonly loading$ = signal<Loading>(false);

  public readonly error$ = signal<ServiceError>(null);

  private cancelRequest$ = new Subject<void>();

  private toNumber(value: unknown): number {
    const parsedValue = Number(value ?? 0);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  private normalizeRecord(record: unknown): P95UtilizationRecord | null {
    if (record === null || typeof record !== 'object') {
      return null;
    }

    const candidate = record as Record<string, unknown>;
    const dimConnectionId = String(candidate.dim_connection_id ?? '').trim();

    if (!dimConnectionId) {
      return null;
    }

    return {
      dim_connection_id: dimConnectionId,
      name_customer_location: String(candidate.name_customer_location ?? ''),
      provisioned_speed_rx_mbps: this.toNumber(
        candidate.provisioned_speed_rx_mbps ?? 0,
      ),
      p95_utilization_rx_pct: this.toNumber(
        candidate.p95_utilization_rx_pct ?? 0,
      ),
      provisioned_speed_tx_mbps: this.toNumber(
        candidate.provisioned_speed_tx_mbps ?? 0,
      ),
      p95_utilization_tx_pct: this.toNumber(
        candidate.p95_utilization_tx_pct ?? 0,
      ),
    };
  }

  private normalizeResponse(response: unknown): Records {
    const responseObject =
      response !== null && typeof response === 'object'
        ? (response as P95UtilizationApiResponse)
        : {};

    const rawItems = Array.isArray(responseObject.items)
      ? responseObject.items
      : [];

    return rawItems
      .map((record) => this.normalizeRecord(record))
      .filter((record): record is P95UtilizationRecord => record !== null);
  }

  async getPage(options: GetPageOptions): Promise<void> {
    this.cancelRequest$.next();
    this.loading$.set(true);
    this.error$.set(null);
    this.records$.set([]);

    try {
      const response = await firstValueFrom(
        this.synapseService$.get95PUtilization({
          duration: options.duration ?? DEFAULT_DURATION,
          showMockData: options.showMockData,
        }).pipe(takeUntil(this.cancelRequest$)),
      );

      const records = this.normalizeResponse(response);
      this.records$.set(records);
    } catch {
      this.records$.set([]);
      this.error$.set('Unable to load utilization data. Please try again.');
    } finally {
      this.loading$.set(false);
    }
  }
}
